import React from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import { useGetReservationByIdQuery } from "../../store/api/reservationDetailSlice";

const ReservationDetail = ({ bookingIdData, onClose }) => {
  const { t } = useTranslation();
  const reservationId = bookingIdData;

  const { data, isLoading, isError, error } = useGetReservationByIdQuery(
    reservationId,
    { skip: !reservationId },
  );

  const detail = data?.data;

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "—";
    return timeStr.slice(0, 5);
  };

  const statusClass = (status) => {
    if (status === "scheduled") return "status pickupCom";
    if (status === "pending") return "status offDuty";
    return "status unavailable";
  };

  const statusLabel = (status) => {
    if (status === "scheduled") return "Reserved";
    if (!status) return "N/A";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <Spinner animation="border" size="sm" className="me-2" />
        {t("loading")}...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 text-center text-danger">
        {error?.data?.message || t("error_occurred")}
        <div className="mt-3">
          <button className="btn btn-secondary btn-sm" onClick={onClose}>
            {t("btns.close")}
          </button>
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="p-4 text-center">
        {t("no")} {t("reservation")} {t("found")}
        <div className="mt-3">
          <button className="btn btn-secondary btn-sm" onClick={onClose}>
            {t("btns.close")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reservation-info p-3">
      <h5 className="mb-3 text-center">
        {t("reservation")} {t("Details")}
      </h5>

      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th>{t("vehicle")}</th>
              <th>{t("school")}</th>
              <th>{t("vendor")}</th>
              <th>{t("driver")}</th>
              <th>{t("students")}</th>
              <th>{t("date")}</th>
              <th>{t("status")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="text-nowrap">
                {detail.vehicle?.vehicle_type_name || "—"}
                {detail.vehicle?.license_number && (
                  <div style={{ fontSize: "0.75rem", color: "#888" }}>
                    {detail.vehicle.license_number}
                  </div>
                )}
              </td>
              <td className="text-nowrap">
                {detail.school?.company_name || detail.school?.full_name || "—"}
              </td>
              <td className="text-nowrap">
                {detail.vendor?.company_name || detail.vendor?.full_name || "—"}
              </td>
              <td className="text-nowrap">
                {detail.driver?.full_name || "—"}
                {detail.driver?.phone && (
                  <div style={{ fontSize: "0.75rem", color: "#888" }}>
                    {detail.driver.phone}
                  </div>
                )}
              </td>
              <td className="text-nowrap">{detail.student_count ?? 0}</td>
              <td className="text-nowrap">
                {formatDate(detail.start_date)}
                {detail.end_date && detail.end_date !== detail.start_date && (
                  <span> – {formatDate(detail.end_date)}</span>
                )}
                {(detail.start_time || detail.end_time) && (
                  <div style={{ fontSize: "0.75rem", color: "#888" }}>
                    {formatTime(detail.start_time)} –{" "}
                    {formatTime(detail.end_time)}
                  </div>
                )}
              </td>
              <td className="text-nowrap">
                <span className={statusClass(detail.status)}>
                  {statusLabel(detail.status)}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Extra info row */}
      <div className="mt-3 row g-2" style={{ fontSize: "0.85rem" }}>
        {detail.booking_type && (
          <div className="col-6 col-sm-4">
            <strong>{t("type")}:</strong>{" "}
            {detail.booking_type === "bus_charter"
              ? "Bus Charter"
              : "Busing Subscription"}
          </div>
        )}
        {detail.pickup_address && (
          <div className="col-6 col-sm-4">
            <strong>{t("pickup")}:</strong> {detail.pickup_address}
          </div>
        )}
        {detail.destination_address && (
          <div className="col-6 col-sm-4">
            <strong>{t("destination")}:</strong> {detail.destination_address}
          </div>
        )}
        {detail.reserved_seats != null && (
          <div className="col-6 col-sm-4">
            <strong>
              {t("reserved")} {t("seats")}:
            </strong>{" "}
            {detail.reserved_seats}
          </div>
        )}
        {detail.notes && (
          <div className="col-12">
            <strong>{t("notes")}:</strong> {detail.notes}
          </div>
        )}
      </div>

      <div className="text-end pt-3">
        <button className="btn btn-secondary" onClick={onClose}>
          {t("btns.close")}
        </button>
      </div>
    </div>
  );
};

export default ReservationDetail;
